// Single source of truth for tier pricing, credits, and tool costs.
// Anything that displays a price, credit count, or recruiter-contact claim in the UI MUST read from here.
// Edge functions have their own copy in supabase/functions/_shared/credits.ts;
// keep the TOOL_COSTS values in sync between the two files.

export type Tier = "free" | "lite" | "pro";

export interface TierInfo {
  label: string;
  priceCents: number;       // 0 for free
  priceDisplay: string;     // pre-formatted ("Free", "$9", "$24")
  cadence: string;          // "/month" or "" for free
  credits: number | null;   // null = unlimited
  creditsDisplay: string;   // "20 credits/month", "60 credits/month", "Unlimited credits"
  hiredContacts: number;
  hiredContactsDisplay: string;
  stripePriceId: string | null;
}

export const TIERS: Record<Tier, TierInfo> = {
  free: {
    label: "Free",
    priceCents: 0,
    priceDisplay: "Free",
    cadence: "",
    credits: 20,
    creditsDisplay: "20 credits/month",
    hiredContacts: 0,
    hiredContactsDisplay: "No recruiter contacts",
    stripePriceId: null,
  },
  lite: {
    label: "Lite",
    priceCents: 900,
    priceDisplay: "$9",
    cadence: "/month",
    credits: 60,
    creditsDisplay: "60 credits/month",
    hiredContacts: 3,
    hiredContactsDisplay: "3 recruiter contacts/month",
    stripePriceId: "price_1TXPITDn88RPvMPE10PVLUAv",
  },
  pro: {
    label: "Pro",
    priceCents: 2400,
    priceDisplay: "$24",
    cadence: "/month",
    credits: null,
    creditsDisplay: "Unlimited credits",
    hiredContacts: 10,
    hiredContactsDisplay: "10 recruiter contacts/month",
    stripePriceId: "price_1TXPIeDn88RPvMPEWPT04Y36",
  },
};

/**
 * The Search Sprint: one payment, 30 days, nothing renews. Not a tier: while
 * it runs the user is on Pro (unlimited credits) with SPRINT.hiredContacts
 * recruiter contacts. MIRRORS supabase/functions/_shared/sprint.ts
 * (SPRINT_PRICE_CENTS, SPRINT_DAYS, SPRINT_HIRED_CAP) and sprint_hired_cap()
 * in migration 20261018120000. The Still Remote pass it includes is the $24,
 * 90-day one (supabase/functions/_shared/directory-lib.ts PRICE_CENTS).
 */
export const SPRINT = {
  label: "Search Sprint",
  priceCents: 2900,
  priceDisplay: "$29",
  days: 30,
  hiredContacts: 15,
  /** The free Sprint is for a layoff within this many days. */
  layoffWindowDays: 60,
  directoryDays: 90,
  directoryPriceDisplay: "$24",
} as const;

// Locked tool costs. MIRROR in supabase/functions/_shared/credits.ts.
// If you change a value here, change it there too.
export const TOOL_COSTS = {
  "resume-tailor": 10,
  "interview-prep": 8,
  "linkedin-analyzer": 5,
  "jobs-chat": 5,
  "smart-match-jobs": 3,
  "market-snapshot": 2,
  "resume-parser": 1,
  "salary-research-basic": 0,
  "salary-research-deep": 1,
} as const;

export type ToolSlug = keyof typeof TOOL_COSTS;
